// Main App component with routing and authentication
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { usePermissionStore } from './lib/permissions';
import { usePermissions } from './lib/permissions';
import { initPermissions } from './lib/auth/initPermissions';
import { useAppInitialization } from './hooks/useAppInitialization';

// Pages
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { AdminPage } from './pages/AdminPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { LanesPage } from './pages/LanesPage';
import { AssetDetailsPage } from './pages/AssetDetailsPage';
import { BeltsPage } from './pages/BeltsPage';
import { PowerBIPage } from './pages/PowerBIPage';
import { CalendarPanel } from './pages/CalendarPanel';
import { Visualization2Page } from './pages/Visualization2Page';
import { Visualization3DPage } from './pages/Visualization3DPage';



// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireCalendarAccess?: boolean;
  require2DVisualization?: boolean;
  require3DVisualization?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  requireCalendarAccess = false,
  require2DVisualization = false,
  require3DVisualization = false
}) => {
  const { isAuthenticated, isLoading, user, error } = useAuthContext();
  const permissions = usePermissions();

  // Show loading while authentication is being determined
  if (isLoading || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-400">Loading authentication...</p>
        </div>
      </div>
    );
  }

  // Show authentication error if user is not authorized for this environment
  if (error && isAuthenticated === false) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-300 mb-6 leading-relaxed">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => {
                // Clear any cached authentication data and redirect to login
                sessionStorage.clear();
                localStorage.clear();
                window.location.href = '/login';
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Try Different Account
            </button>
            <p className="text-sm text-gray-400">
              Or contact your system administrator for assistance
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Only redirect to login if we're sure user is not authenticated and there's no error
  if (isAuthenticated === false) {
    return <Navigate to="/login" replace />;
  }

  // For admin routes, check admin access
  if (requireAdmin) {
    // Show loading while user data is being loaded
    if (!user) {
      return (
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-gray-400">Loading user data...</p>
          </div>
        </div>
      );
    }

    // Check if user has admin panel access using granular permissions
    const hasAdminAccess = permissions.hasAdminAccess();

    if (!hasAdminAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // For calendar routes, check calendar access
  if (requireCalendarAccess) {
    // Show loading while user data is being loaded
    if (!user) {
      return (
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-center">Loading user data...</p>
          </div>
        </div>
      );
    }

    // Check if user has calendar access using granular permissions
    const hasCalendarAccess = permissions.canAccessModule('calendar');

    if (!hasCalendarAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // For 2D visualization routes, check 2D visualization access
  if (require2DVisualization) {
    // Show loading while user data is being loaded
    if (!user) {
      return (
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-center">Loading user data...</p>
          </div>
        </div>
      );
    }

    // Check if user has 2D visualization access using modern permission system
    const has2DVisualizationAccess = permissions.hasPermission('2d_visualisation_access');

    if (!has2DVisualizationAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // For 3D visualization routes, check 3D visualization access
  if (require3DVisualization) {
    // Show loading while user data is being loaded
    if (!user) {
      return (
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-center">Loading user data...</p>
          </div>
        </div>
      );
    }

    // Check if user has 3D visualization access using modern permission system
    const has3DVisualizationAccess = permissions.hasPermission('3d_visualisation_access');

    if (!has3DVisualizationAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// Main App Component with Pre-initialization
const App: React.FC = () => {
  const { isInitialized, error } = useAppInitialization();

  // Show loading screen while MSAL initializes
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Initializing Application...
          </h2>
          <p className="mt-2 text-gray-600">
            Setting up authentication and security
          </p>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 font-medium">Initialization Error:</p>
              <p className="text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Only render the app after MSAL is fully initialized
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
        </div>
      </Router>
    </AuthProvider>
  );
};

// Separate component for routes (to use auth context inside)
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, user, getAccessToken } = useAuthContext();

  // Initialize permissions when app starts
  React.useEffect(() => {
    // NOTE: initPermissions is now called from AuthContext with proper account parameter
    // This call is removed to prevent duplicate initialization with null account
    // initPermissions handled by AuthContext
  }, [isAuthenticated, getAccessToken]);

  // Show loading spinner while MSAL is initializing or authentication is being determined
  if (isLoading || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect unauthenticated users to login page
  if (isAuthenticated === false) {
    const currentPath = window.location.pathname;
    if (currentPath !== '/login') {
      window.location.href = '/login';
      return null;
    }
  }

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        
        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/checkpoint/:checkpointId/lanes" 
          element={
            <ProtectedRoute>
              <LanesPage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/checkpoint/:checkpointId/lane/:laneAssignmentId/assets" 
          element={
            <ProtectedRoute>
              <AssetDetailsPage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/belts" 
          element={
            <ProtectedRoute>
              <BeltsPage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/powerbi" 
          element={
            <ProtectedRoute>
              <PowerBIPage />
            </ProtectedRoute>
          } 
        />

        {/* Visualization v2 Route */}
        <Route 
          path="/visualization2" 
          element={
            <ProtectedRoute require2DVisualization={true}>
              <Visualization2Page />
            </ProtectedRoute>
          } 
        />

        {/* Visualization 3D Route */}
        <Route 
          path="/visualization3d" 
          element={
            <ProtectedRoute require3DVisualization={true}>
              <Visualization3DPage />
            </ProtectedRoute>
          } 
        />

        {/* Calendar Panel Route */}
        <Route 
          path="/calendar-panel" 
          element={
            <ProtectedRoute requireCalendarAccess={true}>
              <CalendarPanel />
            </ProtectedRoute>
          } 
        />

        {/* Admin Dashboard Route */}
        <Route 
          path="/admin-dashboard" 
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboardPage />
            </ProtectedRoute>
          } 
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#374151',
            color: '#ffffff',
            border: '1px solid #4b5563'
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#ffffff'
            }
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff'
            }
          }
        }}
      />
    </>
  );
};

export default App;